import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { MessageCircleQuestion, Quote, Send, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { StatefulButton } from "./StatefulButton";
import { api } from "../lib/api";
import { formatCredits } from "../lib/format";
import { useDialogFocus } from "../lib/useDialogFocus";
import type { MarginNote } from "../types";

const MAX_QUESTION_LENGTH = 300;

/**
 * Заметки на полях: вопрос рассказчику по конкретному месту истории.
 * Заметки приватны — сервер отдаёт только те, что задал этот читатель.
 */
export function useMarginNotes(storyId: string | undefined, enabled: boolean) {
  const [notes, setNotes] = useState<MarginNote[]>([]);
  const [asking, setAsking] = useState(false);
  // Порядок цены вопроса считает сервер: в демо-режиме он честно отдаёт ноль.
  const [estimateCredits, setEstimateCredits] = useState<number | null>(null);

  useEffect(() => {
    if (!storyId || !enabled) { setNotes([]); return; }
    let alive = true;
    api<{ notes: MarginNote[]; estimateCredits?: number }>(`/api/margin-notes?storyId=${encodeURIComponent(storyId)}`)
      .then((data) => { if (!alive) return; setNotes(data.notes); setEstimateCredits(data.estimateCredits ?? null); })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [storyId, enabled]);

  const ask = useCallback(async (quote: string, question: string) => {
    if (!storyId) return null;
    setAsking(true);
    try {
      const data = await api<{ note: MarginNote }>("/api/margin-notes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ storyId, quote, question }),
      });
      setNotes((items) => [...items, data.note]);
      return data.note;
    } finally {
      setAsking(false);
    }
  }, [storyId]);

  return { notes, ask, asking, estimateCredits };
}

export function MarginNoteDialog({ quote, asking, estimateCredits, onClose, onSubmit, night = false }: {
  night?: boolean; quote: string; asking: boolean; estimateCredits: number | null; onClose: () => void; onSubmit: (question: string) => void;
}) {
  const reduceMotion = useReducedMotion();
  const [question, setQuestion] = useState("");
  const dialogRef = useDialogFocus<HTMLDivElement>(true, onClose);
  const ready = question.trim().length >= 3 && !asking;

  return createPortal(
    <motion.div
      className="margin-ask-backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: reduceMotion ? 0 : 0.22 }}
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      <motion.div
        ref={dialogRef}
        className={`margin-ask-dialog${night ? " margin-ask-night" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Спросить на полях"
        tabIndex={-1}
        initial={reduceMotion ? false : { opacity: 0, y: 18, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 12, scale: 0.99 }}
        transition={{ duration: reduceMotion ? 0 : 0.26 }}
      >
        <header>
          <span><MessageCircleQuestion size={17} /> Спросить на полях</span>
          <button type="button" onClick={onClose} aria-label="Закрыть"><X size={16} /></button>
        </header>
        <blockquote><Quote size={14} />{quote}</blockquote>
        <label>
          <span>Что хотите уточнить?</span>
          <textarea
            autoFocus
            data-dialog-initial-focus
            disabled={asking}
            rows={3}
            maxLength={MAX_QUESTION_LENGTH}
            value={question}
            placeholder="Например: а что стало с ним дальше?"
            onChange={(event) => setQuestion(event.target.value)}
            onKeyDown={(event) => { if (event.key === "Enter" && (event.metaKey || event.ctrlKey) && ready) onSubmit(question.trim()); }}
          />
          <small>{question.length} / {MAX_QUESTION_LENGTH}</small>
        </label>
        <footer>
          <p>Ответ появится сноской рядом с этим местом и будет виден только вам.
            {estimateCredits !== null && (estimateCredits > 0
              ? <em className="ask-price">≈ {formatCredits(estimateCredits)} кр. · точная цена спишется по факту</em>
              : <em className="ask-price">Демо-режим: вопрос бесплатный</em>)}
          </p>
          <StatefulButton className="primary-button" disabled={!ready} state={asking ? "pending" : "idle"} pendingLabel="Готовим ответ…" onClick={() => onSubmit(question.trim())}>
            <span>Спросить</span><Send size={15} />
          </StatefulButton>
        </footer>
      </motion.div>
    </motion.div>,
    document.body,
  );
}

const HINT_SEEN_KEY = "because-fm-margin-hint-v1";

function hintSeen() {
  try { return localStorage.getItem(HINT_SEEN_KEY) === "1"; } catch { return true; }
}

function rememberHint() {
  try { localStorage.setItem(HINT_SEEN_KEY, "1"); } catch { /* приватный режим не должен ломать чтение */ }
}

/** Подсказка о заметках на полях: иначе механику находят только случайно. */
export function MarginHint({ enabled, estimateCredits }: { enabled: boolean; estimateCredits: number | null }) {
  const [visible, setVisible] = useState(() => enabled && !hintSeen());
  if (!visible || !enabled) return null;
  return <aside className="margin-hint">
    <MessageCircleQuestion size={16} aria-hidden="true" />
    <p><b>В тексте можно задавать вопросы.</b> Выделите фрагмент — ответ появится рядом и будет виден только вам.{estimateCredits === 0 ? " В демо-режиме это бесплатно." : estimateCredits ? ` Вопрос стоит около ${formatCredits(estimateCredits)} кр.` : ""}</p>
    <button type="button" aria-label="Больше не показывать подсказку" onClick={() => { rememberHint(); setVisible(false); }}><X size={15} /></button>
  </aside>;
}

export function MarginNoteCard({ note }: { note: MarginNote }) {
  return <aside className="margin-note" aria-label="Заметка на полях">
    <header><MessageCircleQuestion size={14} /><b>{note.question}</b></header>
    <p>{note.answer}</p>
  </aside>;
}

/** Плашка над выделением. Появляется только когда в тексте что-то выделено мышью. */
export function MarginAskChip({ onAsk }: { onAsk: (quote: string) => void }) {
  const [chip, setChip] = useState<{ quote: string; top: number; left: number } | null>(null);

  useEffect(() => {
    function readSelection() {
      const selection = window.getSelection();
      const text = selection?.toString().replace(/\s+/g, " ").trim() || "";
      if (!selection || selection.rangeCount === 0 || text.length < 12) { setChip(null); return; }
      const range = selection.getRangeAt(0);
      const container = range.commonAncestorContainer;
      const element = container.nodeType === 1 ? container as Element : container.parentElement;
      if (!element?.closest(".story-body")) { setChip(null); return; }
      const rect = range.getBoundingClientRect();
      if (!rect.width || !rect.height || rect.bottom < 0 || rect.top > window.innerHeight) { setChip(null); return; }
      setChip({ quote: text, top: rect.top >= 52 ? rect.top - 46 : Math.min(window.innerHeight - 48, rect.bottom + 8), left: Math.max(110, Math.min(window.innerWidth - 110, rect.left + rect.width / 2)) });
    }
    document.addEventListener("selectionchange", readSelection);
    document.addEventListener("pointerup", readSelection);
    window.addEventListener("scroll", readSelection, true);
    window.addEventListener("resize", readSelection);
    return () => {
      document.removeEventListener("selectionchange", readSelection);
      document.removeEventListener("pointerup", readSelection);
      window.removeEventListener("scroll", readSelection, true);
      window.removeEventListener("resize", readSelection);
    };
  }, []);

  if (!chip) return null;
  return createPortal(
    <button
      type="button"
      className="margin-ask-chip"
      style={{ top: `${Math.max(12, chip.top)}px`, left: `${chip.left}px` }}
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => { onAsk(chip.quote); setChip(null); window.getSelection()?.removeAllRanges(); }}
    ><MessageCircleQuestion size={14} /> Спросить на полях</button>,
    document.body,
  );
}

export function MarginNotesLayer({ night = false, storyId, enabled, notes, asking, estimateCredits, ask, pending, setPending, onToast, onAuth }: {
  night?: boolean;
  storyId: string | undefined;
  enabled: boolean;
  notes: MarginNote[];
  asking: boolean;
  estimateCredits: number | null;
  ask: (quote: string, question: string) => Promise<MarginNote | null>;
  pending: string | null;
  setPending: (quote: string | null) => void;
  onToast: (message: string) => void;
  onAuth: () => void;
}) {
  void notes; void storyId;
  async function submit(question: string) {
    if (!pending) return;
    try {
      await ask(pending, question);
      rememberHint();
      setPending(null);
      onToast("Ответ добавлен к тексту");
    } catch (error) { onToast((error as Error).message); }
  }
  return <>
    <MarginAskChip onAsk={(quote) => { if (enabled) setPending(quote); else { onAuth(); onToast("Войдите, чтобы сохранять ответы к тексту"); } }} />
    <AnimatePresence>{pending && <MarginNoteDialog night={night} quote={pending} asking={asking} estimateCredits={estimateCredits} onClose={() => setPending(null)} onSubmit={submit} />}</AnimatePresence>
  </>;
}
